/**
 * Bazaarvoice display integration helpers (PDP loader URL, locale mapping, product IDs).
 */

const BV_LOADER_HOST = 'https://apps.bazaarvoice.com';
const BV_ENVIRONMENT_URL = {
  Staging: 'staging',
  Production: 'production',
};
const BV_LOCALE_PAIR = /^(default|[a-z]{2}|[a-z]{2}_[a-zA-Z]{2})[\s]*:[\s]*([\w\s%]*\/)?[a-z]{2}_[a-zA-Z]{2}$/;
const BV_LOCALE_FULL = /^[a-z]{2}_[a-zA-Z]{2}$/;

/**
 * MRT client data may serialize SFCC enum-of-string prefs as { value, displayValue }.
 *
 * @param {string|object|null|undefined} prefValue
 * @returns {string|null}
 */
export function normalizeSitePreferenceValue(prefValue) {
  if (prefValue == null || prefValue === '') {
    return null;
  }

  if (typeof prefValue === 'object' && prefValue.value != null) {
    return String(prefValue.value);
  }

  return String(prefValue);
}

/**
 * @param {object} [bazaarvoicePrefs]
 * @returns {object|null}
 */
export function normalizeBazaarvoicePrefs(bazaarvoicePrefs) {
  if (!bazaarvoicePrefs) {
    return null;
  }

  return {
    customerName: normalizeSitePreferenceValue(bazaarvoicePrefs.customerName),
    deploymentZone: normalizeSitePreferenceValue(bazaarvoicePrefs.deploymentZone),
    environment: normalizeSitePreferenceValue(bazaarvoicePrefs.environment),
    localeMapping: Array.isArray(bazaarvoicePrefs.localeMapping)
      ? bazaarvoicePrefs.localeMapping.map((entry) => normalizeSitePreferenceValue(entry))
      : [],
  };
}

/**
 * @param {string} value
 * @returns {string}
 */
function trimMappingEntry(value) {
  return String(value || '').replace(/^[\s"]+|[\s"]+$/g, '');
}

/**
 * Resolves Bazaarvoice locale (and optional deployment zone override) from site preference mapping.
 *
 * @param {string} localeId - Current storefront locale id (e.g. en-AU)
 * @param {string[]} localeMapping - bvLocaleMapping site preference
 * @param {string} [defaultLocaleId] - Site default locale id
 * @returns {{bvLocale: string|null, deploymentZone: string|null}}
 */
export function resolveBazaarvoiceLocale(localeId, localeMapping = [], defaultLocaleId) {
  const currentLocale = localeId || '';
  const isDefaultLocale = defaultLocaleId && currentLocale === defaultLocaleId;
  let bvLocale = null;
  let deploymentZone = null;

  if (!Array.isArray(localeMapping) || localeMapping.length === 0) {
    return {bvLocale: null, deploymentZone: null};
  }

  if (localeMapping.length === 1) {
    const item = trimMappingEntry(localeMapping[0]);

    if (BV_LOCALE_FULL.test(item)) {
      return {bvLocale: item, deploymentZone: null};
    }

    if (BV_LOCALE_PAIR.test(item)) {
      const [dwLocale, mapped] = item.split(':').map((part) => trimMappingEntry(part));
      if (dwLocale === currentLocale || (dwLocale === 'default' && isDefaultLocale)) {
        return parseLocaleMappingValue(trimMappingEntry(mapped));
      }
    }

    return {bvLocale: null, deploymentZone: null};
  }

  for (let i = 0; i < localeMapping.length; i++) {
    const item = trimMappingEntry(localeMapping[i]);

    if (BV_LOCALE_FULL.test(item) && isDefaultLocale) {
      bvLocale = item;
      break;
    }

    if (BV_LOCALE_PAIR.test(item)) {
      const [dwLocale, mapped] = item.split(':').map((part) => trimMappingEntry(part));
      if (dwLocale === currentLocale) {
        const parsed = parseLocaleMappingValue(trimMappingEntry(mapped));
        bvLocale = parsed.bvLocale;
        deploymentZone = parsed.deploymentZone;
        break;
      }
    }
  }

  return {bvLocale, deploymentZone};
}

/**
 * @param {string} mappedValue - BV locale or zone/locale pair from mapping
 * @returns {{bvLocale: string|null, deploymentZone: string|null}}
 */
function parseLocaleMappingValue(mappedValue) {
  if (!mappedValue) {
    return {bvLocale: null, deploymentZone: null};
  }

  if (mappedValue.indexOf('/') !== -1) {
    const [zone, locale] = mappedValue.split('/');
    return {
      deploymentZone: decodeURIComponent(trimMappingEntry(zone)),
      bvLocale: trimMappingEntry(locale),
    };
  }

  return {bvLocale: mappedValue, deploymentZone: null};
}

/**
 * @param {object} [bazaarvoicePrefs] - Raw site preference group (enum values may be { value, displayValue })
 * @param {string} localeId
 * @param {string} [defaultLocaleId]
 * @returns {string|null}
 */
export function getBazaarvoiceLoaderUrl(bazaarvoicePrefs, localeId, defaultLocaleId) {
  const {
    customerName,
    deploymentZone,
    environment,
    localeMapping,
  } = normalizeBazaarvoicePrefs(bazaarvoicePrefs) || {};

  if (!customerName || !deploymentZone || !environment) {
    return null;
  }

  const environmentSegment = BV_ENVIRONMENT_URL[environment];
  if (!environmentSegment) {
    return null;
  }

  const {bvLocale, deploymentZone: mappedZone} = resolveBazaarvoiceLocale(
    localeId,
    localeMapping,
    defaultLocaleId
  );
  const localeSegment = bvLocale || localeId?.replace('-', '_');

  if (!localeSegment) {
    return null;
  }

  const siteId = mappedZone || deploymentZone;
  const encodedClient = encodeURIComponent(customerName);
  const encodedSite = encodeURIComponent(siteId);
  const encodedLocale = encodeURIComponent(localeSegment);

  return `${BV_LOADER_HOST}/deployments/${encodedClient}/${encodedSite}/${environmentSegment}/${encodedLocale}/bv.js`;
}

/**
 * Product id sent to Bazaarvoice must match the product catalog feed ExternalId.
 *
 * @param {object} product
 * @returns {string|null}
 */
export function getBazaarvoiceProductId(product) {
  if (!product) {
    return null;
  }

  const represented = product.representedProduct;
  const masterId =
    product.master?.masterId ??
    represented?.master?.masterId;
  const id =
    product.id ??
    product.productId ??
    represented?.id ??
    represented?.productId;

  if (masterId) {
    return masterId;
  }

  return id || null;
}

/**
 * Hash target for review count link on PDP.
 */
export const BAZAARVOICE_REVIEWS_SECTION_ID = 'product-bazaarvoice-reviews';

/**
 * @param {object} [bazaarvoicePrefs]
 * @returns {boolean}
 */
export function isBazaarvoiceConfigured(bazaarvoicePrefs) {
  const normalized = normalizeBazaarvoicePrefs(bazaarvoicePrefs);

  return Boolean(
    normalized?.customerName &&
    normalized?.deploymentZone &&
    normalized?.environment
  );
}
